import {
  Box,
  Flex,
  IconButton,
  HStack,
  Image,
  Text,
  Spinner,
} from '@chakra-ui/react';
import {
  FiSend,
  FiPaperclip,
  FiMic,
  FiX,
  FiTrash2,
  FiPause,
  FiSquare,
  FiFile,
  FiFileText,
} from 'react-icons/fi';
import { useState, useRef, useEffect } from 'react';
import ResizeTextarea from 'react-textarea-autosize';
import { LiveAudioVisualizer } from 'react-audio-visualize';
import { Message, MessageAttachment } from '@/features/messages/messages.types';

import { useMessageAttachments } from '@/hooks/useMessageAttachments';
import { useWhatsappMessages } from '@/hooks/WAHA/useWhatsappMessages';
import { useWhatsappMedia } from '@/hooks/WAHA/useWhatsappMedia';
import { ProgressRoot, ProgressValueText } from '@/components/ui/progress';
import { Avatar } from '@/components/ui/avatar';

import { CurrentUser } from '@/features/users/users.types';
import { Conversation } from '@/features/conversations/conversations.types';
import { createMessage, updateMessage } from '@/features/messages/messages.service';
import { fetchChannel } from '@/features/channels/channels.service';
import { persistStorageService } from '@/features/auth/persist-storage';
import { OrgLocation } from '@/features/organizations/organizations.types';
import { useMessageAttachmentUpload } from '@/features/whatsapp/whatsapp.hook';

// Extended Conversation interface to include channel_id
interface ExtendedConversation extends Conversation {
  channel_id?: string;
  wab_chatId_external?: string;
}

// Extended User interface to include email and phone_number
interface ConversationUser {
  id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  avatar?: string;
  phone_number?: string;
}

interface FileToUpload {
  file: File | Blob;
  type: 'image' | 'file' | 'audio' | 'text';
  duration?: number;
}

interface MessageInputProps {
  conversationId: number | string;
  conversation: ExtendedConversation;
  isDisabled?: boolean;
  currentUser: CurrentUser;
  onMessageSent: (message: Message) => void;
  isAgentActive?: boolean;
  onTakeoverClick?: () => void;
}

interface AttachmentFile {
  type: 'image' | 'file' | 'audio' | 'video';
  url: string;
  name: string;
  file: File | Blob;
  duration_audio?: number;
  fileType?: string;
  // Add upload progress tracking
  uploadProgress?: number;
  isUploading?: boolean;
  error?: string;
}

export function MessageInput({
  conversationId,
  conversation,
  isDisabled = false,
  currentUser,
  onMessageSent,
  onTakeoverClick,
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [overallUploadProgress, setOverallUploadProgress] = useState(0);

  // Import the useMessageAttachments hook
  const {
    uploadMultipleAttachments,
    createAttachmentRecords,
    convertAudioToOpus,
    isUploading,
    uploadProgress: hookUploadProgress,
  } = useMessageAttachments();

  // Use a valid placeholder or a local asset path
  const defaultAvatar = '/assets/default-avatar.png'; // Example using a local asset
  // OR const defaultAvatar = 'https://ui-avatars.com/api/?name=?'; // Example using a service
  const currentUserAvatar = currentUser?.avatar || defaultAvatar;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize WhatsApp hooks
  const [channelSession, setChannelSession] = useState<string>('default');
  const { sendTextMessage } = useWhatsappMessages(channelSession);
  const processMessageAttachmentAudio = useMessageAttachmentUpload();

  const { sendMedia, sendFile, sendVideo, sendVoice } =
    useWhatsappMedia(channelSession);

  // Load channel details when conversation changes
  useEffect(() => {
    if (conversation?.channel_id) {
      const loadChannelDetails = async () => {
        try {
          const { data: channel } = await fetchChannel({
            id: conversation.channel_id as string,
          });

          if (channel?.type === 'whatsapp') {
            // Get session name from channel_config if available, fallback to channel_id
            const sessionName = channel.channel_config?.sessionId || channel.id;
            setChannelSession(sessionName as string);
          }
        } catch (error) {
          console.error('Error loading channel details:', error);
        }
      };

      loadChannelDetails();
    }
  }, [conversation?.channel_id]);

  // Update upload progress when the hook reports progress
  useEffect(() => {
    setOverallUploadProgress(hookUploadProgress);
  }, [hookUploadProgress]);

  // Format recording duration
  const formatDuration = (seconds: number) => {
    console.log('formatDuration duration_audio', seconds);
    if (seconds === undefined || seconds === null) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      stopRecording();
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, []);

  // TODO: Move this to a separate file and fix the message disappearance bug
  const handleSend = async () => {
    if ((message.trim() || attachments.length > 0) && !isSending) {
      // Move these declarations BEFORE the try block
      const messageText = message.trim();
      const currentAttachments = [...attachments];
      const selectedOrg = persistStorageService.getPersistItem<OrgLocation>({
        name: 'selectedOrganisation',
        parse: true,
      });

      const selectedLoc = persistStorageService.getPersistItem<OrgLocation>({
        name: 'selectedLocation',
        parse: true,
      });

      const currentTimestamp = new Date().toISOString();
      const tempMessageId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      const optimisticMessage = {
        id: tempMessageId,
        content: messageText,
        created_at: currentTimestamp,
        conversation_id: conversationId.toString(),
        channel_id: conversation.channel_id,
        sender_id: currentUser.id,
        sender_name: currentUser.fullName,
        from_agent: false,
        receiver_id: conversation?.user?.id || null,
        receiver_name:
          (conversation?.user as ConversationUser)?.email || 'Customer',
        is_read: true,
        read_at: currentTimestamp,
        message_type:
          currentAttachments.length > 0
            ? currentAttachments.some((att) => att.type === 'audio')
              ? 'audio'
              : 'attachments'
            : 'text',
        organisation_id: selectedOrg?.id || '',
        location_id: selectedLoc?.id || '',
        attachments: [],
        _status: 'pending' as const,
      } as Message;

      try {
        setIsSending(true);

        let messageType = 'text';
        if (currentAttachments.length > 0) {
          const hasAudio = currentAttachments.some(
            (att) => att.type === 'audio',
          );
          messageType = hasAudio ? 'audio' : 'attachments';
        }

        setMessage('');
        setAttachments([]);

        const context = {
          organizationId: selectedOrg?.id || '',
          locationId: selectedLoc?.id || '',
          userId: currentUser?.id || '',
        };

        const conversationUser: ConversationUser = conversation?.user || {
          id: '',
          first_name: '',
          last_name: '',
          email: 'Customer',
        };

        onMessageSent(optimisticMessage);

        let uploadedAttachments: Partial<MessageAttachment>[] = [];
        if (currentAttachments.length > 0) {
          onMessageSent({
            ...optimisticMessage,
            _status: 'sending' as const,
          } as Message);

          const filesToUpload = currentAttachments.map((attachment) => ({
            file: attachment.file,
            type:
              attachment.type === 'video' ? ('text' as const) : attachment.type,
            duration_audio: attachment.duration_audio,
          }));

          uploadedAttachments = await uploadMultipleAttachments(
            filesToUpload as FileToUpload[],
            conversationId.toString(),
            context,
          );

          if (uploadedAttachments.length > 0) {
            onMessageSent({
              ...optimisticMessage,
              attachments: uploadedAttachments as MessageAttachment[],
              _status: 'sent' as const,
            } as Message);
          }
        } else {
          onMessageSent({
            ...optimisticMessage,
            _status: 'sent' as const,
          } as Message);
        }

        const newMessagePayload = {
          conversation_id: conversationId.toString(),
          channel_id: conversation.channel_id,
          content: messageText,
          sender_id: currentUser.id,
          sender_name: currentUser.fullName,
          from_agent: false,
          receiver_id: conversationUser.id || null,
          receiver_name: conversationUser.first_name
            ? `${conversationUser.first_name} ${conversationUser.last_name || ''}`.trim()
            : conversationUser.email || 'Customer',
          is_read: true,
          read_at: currentTimestamp,
          message_type: messageType,
          attachments: uploadedAttachments as MessageAttachment[],
          organisation_id: selectedOrg?.id || '',
          location_id: selectedLoc?.id || '',
        };

        // Send the actual message via dataProvider
        const result = await createMessage(newMessagePayload);

        if (result && result.data) {
          let finalMessage = result.data as Message;

          if (uploadedAttachments.length > 0 && finalMessage.id) {
            const dbAttachments = await createAttachmentRecords(
              finalMessage.id,
              uploadedAttachments,
            );
            finalMessage = {
              ...finalMessage,
              attachments: dbAttachments,
            };
          }

          onMessageSent({
            ...finalMessage,
            _tempId: tempMessageId,
            _status: 'delivered' as const,
          });

          // Send to WhatsApp if it's a WhatsApp channel
          if (
            conversation.channel_id &&
            channelSession &&
            channelSession !== 'default'
          ) {
            try {
              const { data: channelData } = await fetchChannel({
                id: conversation.channel_id,
              });

              if (channelData?.type === 'whatsapp') {
                console.log('WhatsApp channel found:', channelData);
                let chatId = (conversation.user as ConversationUser)
                  ?.phone_number;

                // Check if wab_chatId_external is available - use it directly for both @c.us and @lid formats
                // WAHA supports sending to both @c.us and @lid formats
                const wabChatId = (conversation as ExtendedConversation).wab_chatId_external;
                if (wabChatId && (wabChatId.includes('@c.us') || wabChatId.includes('@lid'))) {
                  // Use wab_chatId_external directly - WAHA supports both formats
                  chatId = wabChatId;
                  console.log('Using wab_chatId_external directly:', wabChatId);
                } else if (chatId) {
                  // Format the phone number to WhatsApp chat ID format
                  // Remove any non-digit characters
                  const cleanPhone = chatId.replace(/\D/g, '');
                  // Add @c.us suffix for individual chats
                  chatId = `${cleanPhone}@c.us`;
                }

                if (chatId) {
                  console.log('Target WhatsApp chatId:', chatId);
                  if (messageText && currentAttachments.length === 0) {
                    console.log('Sending WhatsApp text message:', {
                      chatId,
                      text: messageText,
                      session: channelSession,
                    });
                    const waResponse = await sendTextMessage({ chatId, text: messageText });
                    console.log('WhatsApp text message response:', waResponse);

                    // Update message with WhatsApp message ID to prevent duplicate creation
                    if (waResponse?.id && finalMessage?.id) {
                      try {
                        await updateMessage({
                          id: finalMessage.id,
                          data: { ...finalMessage, wab_message_id_external: waResponse.id },
                          previousData: finalMessage,
                        });
                        console.log('Message updated with wab_message_id_external:', waResponse.id);
                      } catch (updateError) {
                        console.error('Failed to update message with WhatsApp ID:', updateError);
                      }
                    }
                  } else if (currentAttachments.length > 0) {
                    let firstWaResponseId: string | null = null; // Track first WAHA response for DB update
                    for (const attachment of currentAttachments) {
                      // Get the file URL from uploadedAttachments
                      const fileUrl = attachment.url?.startsWith('blob:')
                        ? uploadedAttachments.find(
                            (a) => a.file_name === attachment.name,
                          )?.file_url
                        : attachment.url;

                      // Create the message payload
                      const mediaMessage = {
                        chatId,
                        file: {
                          mimetype:
                            attachment.file.type || 'application/octet-stream',
                          url: fileUrl,
                          filename: attachment.name,
                        },
                        caption: messageText || undefined,
                      };

                      // Determine the type of attachment
                      const isImage = attachment.type === 'image';
                      const isVideo =
                        attachment.file.type?.startsWith('video/');
                      const isAudio = attachment.type === 'audio';

                      if (isVideo) {
                        console.log(
                          'Sending WhatsApp video message:',
                          mediaMessage,
                        );
                        const videoResponse = await sendVideo(mediaMessage);
                        if (!firstWaResponseId && videoResponse?.id) {
                          firstWaResponseId = videoResponse.id;
                        }
                        console.log(
                          `WhatsApp video sent for ${attachment.name}`,
                        );
                      } else if (isAudio) {
                        console.log(
                          'Processing WhatsApp audio message. Initial mediaMessage:',
                          mediaMessage,
                        );
                        try {
                          const audioAttachment =
                            uploadedAttachments.length > 0
                              ? uploadedAttachments[0]
                              : null;

                          console.log('Audio filename comparison:', {
                            clientSideName: attachment.name,
                            availableAttachments: uploadedAttachments.map(
                              (a) => a.file_name,
                            ),
                          });

                          if (!audioAttachment || !audioAttachment.file_url) {
                            console.error(
                              'ERROR: Could not find any valid audio attachment in uploadedAttachments.',
                              { uploadedAttachments },
                            );
                            throw new Error(
                              'No valid audio attachment found to convert.',
                            );
                          }

                          const supabaseFileUrl = audioAttachment.file_url;
                          console.log(
                            'STEP 1: Found Supabase file URL for conversion:',
                            supabaseFileUrl,
                          );

                          try {
                            console.log(
                              'STEP 2: Calling convertAudioToOpus. WAITING for response...',
                            );

                            // Check if the function is available
                            if (typeof convertAudioToOpus !== 'function') {
                              throw new Error(
                                'convertAudioToOpus is not a function',
                              );
                            }

                            // Call the function and await its response
                            const convertResponse =
                              await convertAudioToOpus(supabaseFileUrl);

                            const response =
                              await processMessageAttachmentAudio.mutateAsync({
                                mediaUrl: supabaseFileUrl,
                                organisation_id: selectedOrg?.id || '',
                                location_id: selectedLoc?.id || '',
                                conversation_id: conversationId.toString(),
                                media_type: 'audio',
                              });

                            console.log(
                              'processMessageAttachmentAudio response',
                              response,
                            );

                            // Log the response more thoroughly
                            console.log(
                              'STEP 3: convertAudioToOpus call FINISHED. Full response:',
                              convertResponse,
                            );
                            console.log(
                              'STEP 3.1: Response JSON:',
                              JSON.stringify(convertResponse || {}),
                            );

                            // Additional logging to debug the response structure
                            console.log('Convert response details:', {
                              hasResponse: !!convertResponse,
                              responseType: typeof convertResponse,
                              responseKeys: convertResponse
                                ? Object.keys(convertResponse)
                                : [],
                              responseStringified:
                                JSON.stringify(convertResponse),
                            });

                            // More robust response validation
                            if (!convertResponse) {
                              throw new Error(
                                'No response received from audio conversion endpoint',
                              );
                            }

                            // Check if we have file property in the response
                            if (!convertResponse.file) {
                              throw new Error(
                                'Response missing required "file" property',
                              );
                            }

                            // Check if file object has data property
                            if (!convertResponse.file.data) {
                              throw new Error(
                                'Response file object missing required "data" property',
                              );
                            }

                            console.log(
                              'STEP 4: Conversion SUCCESSFUL. Valid data received.',
                            );
                            console.log(
                              'Data length:',
                              typeof convertResponse.file.data === 'string'
                                ? convertResponse.file.data.length
                                : 'not a string',
                            );

                            const opusMediaMessage = {
                              chatId,
                              file: {
                                mimetype: 'audio/ogg; codecs=opus',
                                filename: 'voice-message.opus',
                                data: convertResponse.file.data,
                              },
                              session: channelSession,
                              caption: messageText || undefined,
                            };

                            console.log(
                              'STEP 6: Sending voice message with converted OPUS data',
                            );
                            const voiceResponse = await sendVoice(opusMediaMessage);
                            if (!firstWaResponseId && voiceResponse?.id) {
                              firstWaResponseId = voiceResponse.id;
                            }
                            console.log(
                              'STEP 7: WhatsApp voice message sent successfully',
                            );
                          } catch (conversionError) {
                            console.error(
                              'Detailed conversion error:',
                              conversionError,
                            );

                            // Try to get more information about the failing conversion
                            console.error(
                              'Original file URL causing conversion failure:',
                              supabaseFileUrl,
                            );

                            // Rethrow the error to be handled by the outer catch block
                            throw new Error(
                              `Audio conversion failed: ${(conversionError as Error)?.message || 'Unknown error'}`,
                            );
                          }
                        } catch (error) {
                          console.error(
                            'FINAL ERROR in audio processing pipeline:',
                            error,
                          );
                          console.warn(
                            'Cannot use fallback as original URL might be invalid or conversion is preferred path.',
                          );
                          console.error(
                            'WhatsApp audio message could not be sent for attachment:',
                            attachment.name,
                          );
                        }
                      } else if (isImage) {
                        console.log(
                          'Sending WhatsApp image message:',
                          mediaMessage,
                        );
                        const imageResponse = await sendMedia(mediaMessage);
                        if (!firstWaResponseId && imageResponse?.id) {
                          firstWaResponseId = imageResponse.id;
                        }
                        console.log(
                          `WhatsApp image sent for ${attachment.name}`,
                        );
                      } else {
                        console.log(
                          'Sending WhatsApp file message:',
                          mediaMessage,
                        );
                        const fileResponse = await sendFile(mediaMessage);
                        if (!firstWaResponseId && fileResponse?.id) {
                          firstWaResponseId = fileResponse.id;
                        }
                        console.log(
                          `WhatsApp file sent for ${attachment.name}`,
                        );
                      }
                    }

                    // Update message with first WhatsApp message ID to prevent duplicate creation
                    if (firstWaResponseId && finalMessage?.id) {
                      try {
                        await updateMessage({
                          id: finalMessage.id,
                          data: { ...finalMessage, wab_message_id_external: firstWaResponseId },
                          previousData: finalMessage,
                        });
                        console.log('Message updated with wab_message_id_external:', firstWaResponseId);
                      } catch (updateError) {
                        console.error('Failed to update message with WhatsApp ID:', updateError);
                      }
                    }
                  }
                } else {
                  console.error(
                    'No valid chatId (phone number) found to send WhatsApp message.',
                  );
                }
              }
            } catch (whatsappError) {
              console.error(
                'Error sending message to WhatsApp:',
                whatsappError,
              );
            }
          }
        } else {
          // Handle case where database creation might have failed silently
          throw new Error(
            'Failed to create message in database or result.data is missing',
          );
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        onMessageSent({
          ...optimisticMessage,
          _status: 'error' as const,
        } as Message);
      } finally {
        setIsSending(false);
        setOverallUploadProgress(0);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileClick = () => {
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Process each selected file
    Array.from(files).forEach((file) => {
      // Default file type is 'file'
      let fileType: 'image' | 'file' | 'audio' | 'video' = 'file';
      let fileSubType: string | undefined;

      // Determine file type based on MIME type
      if (file.type.startsWith('image/')) {
        fileType = 'image';
      } else if (file.type.startsWith('audio/')) {
        fileType = 'audio';
      } else if (file.type.startsWith('video/')) {
        fileType = 'video';
      } else if (file.type === 'application/pdf') {
        fileSubType = 'pdf';
      } else if (
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        fileSubType = 'docx';
      }

      // Create a new attachment with initial state
      const newAttachment: AttachmentFile = {
        type: fileType,
        url: URL.createObjectURL(file),
        name: file.name,
        file: file,
        fileType: fileSubType,
        uploadProgress: 0,
        isUploading: false,
        error: undefined,
      };

      // Add to attachments state
      setAttachments((prev) => [...prev, newAttachment]);
    });

    // Reset the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);

      // Reset recording duration
      setRecordingDuration(0);

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Clear any previous onstop handler
      recorder.onstop = null;

      // Start recording
      audioChunksRef.current = [];
      recorder.start();
      setIsRecording(true);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Capture the final duration before stopping
      const finalDuration = recordingDuration;

      mediaRecorderRef.current.stop();
      // Stop all tracks
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      setIsRecording(false);

      // Create audio attachment with the final duration after mediaRecorder stops
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/wav',
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // Create an audio attachment with the captured duration
        const timestamp = new Date().toISOString().replace(/:/g, '-');
        const newAttachment: AttachmentFile = {
          type: 'audio',
          url: url,
          name: `voice-message-${timestamp}.wav`,
          file: audioBlob,
          duration_audio: finalDuration, // Use the final duration captured when stopping
          uploadProgress: 0,
          isUploading: false,
        };

        // Add the voice message to attachments
        setAttachments([newAttachment]);
      };
    }
  };

  // Cancel recording
  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Stop recording without saving
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());

      // Clear timer and reset
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      setIsRecording(false);
      setRecordingDuration(0);
      audioChunksRef.current = [];
    }
  };

  // Toggle recording when mic is clicked
  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const removeAttachment = (index: number) => {
    // If removing an audio attachment, revoke the URL
    const attachment = attachments[index];
    if (attachment.type === 'audio' && attachment.url) {
      URL.revokeObjectURL(attachment.url);
      setAudioUrl(null);
      setRecordingDuration(0);
    }

    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box
      p={4}
      borderTop="1px solid"
      borderTopColor="gray.200"
      bg="white"
      position="absolute"
      bottom="0"
      left="0"
      right="0"
      zIndex={10}
      // Single click handler on the outermost container
      onClick={isDisabled ? onTakeoverClick : undefined}
      cursor={isDisabled ? 'pointer' : 'default'}
      _hover={isDisabled ? { bg: 'gray.50' } : {}}
    >
      {/* Attachment previews */}
      {attachments.length > 0 && (
        <Flex mb={3} overflowX="auto" pb={2}>
          <HStack gap={2}>
            {attachments.map((attachment, index) => (
              <Box
                key={index}
                position="relative"
                borderRadius="md"
                overflow="hidden"
                border="1px"
              >
                {attachment.type === 'image' && (
                  <Box position="relative">
                    <Image
                      src={attachment.url}
                      alt={attachment.name}
                      boxSize="60px"
                      objectFit="cover"
                    />
                    {/* Upload progress overlay */}
                    {isUploading && (
                      <Flex
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        bg="rgba(0,0,0,0.4)"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Spinner size="lg" color="white" />
                      </Flex>
                    )}
                  </Box>
                )}
                {attachment.type === 'video' && (
                  <Box position="relative">
                    <Box
                      width="120px"
                      height="80px"
                      bg="gray.100"
                      borderRadius="md"
                      overflow="hidden"
                      position="relative"
                    >
                      <video
                        src={attachment.url}
                        controls
                        width="100%"
                        height="100%"
                        style={{ objectFit: 'cover', backgroundColor: 'black' }}
                      />
                      {/* Upload progress overlay */}
                      {isUploading && (
                        <Flex
                          position="absolute"
                          top={0}
                          left={0}
                          right={0}
                          bottom={0}
                          bg="rgba(0,0,0,0.4)"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Spinner size="lg" color="white" />
                        </Flex>
                      )}
                    </Box>
                    <Text
                      fontSize="xs"
                      mt={1}
                      textAlign="center"
                      color="gray.600"
                    >
                      {attachment.name}
                    </Text>
                  </Box>
                )}
                {attachment.type === 'file' && (
                  <Box
                    boxSize="60px"
                    bg="gray.100"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    p={1}
                    position="relative"
                  >
                    {attachment.fileType === 'pdf' ? (
                      <FiFile size={20} color="red" />
                    ) : attachment.fileType === 'docx' ? (
                      <FiFileText size={20} color="blue" />
                    ) : (
                      <FiPaperclip size={20} />
                    )}
                    <Text fontSize="xs" textAlign="center" mt={1}>
                      {attachment.name}
                    </Text>
                    {/* Upload progress overlay */}
                    {isUploading && (
                      <Flex
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        bg="rgba(0,0,0,0.4)"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Spinner size="lg" color="white" />
                      </Flex>
                    )}
                  </Box>
                )}
                {attachment.type === 'audio' && (
                  <Box
                    width="200px"
                    bg="gray.50"
                    p={2}
                    borderRadius="md"
                    position="relative"
                  >
                    <HStack>
                      <Box
                        bg="gray.200"
                        p={2}
                        borderRadius="md"
                        fontSize="xs"
                        fontWeight="bold"
                      >
                        {attachment.duration_audio || 0}
                      </Box>
                      <Box flex="1">
                        {isUploading ? (
                          <ProgressRoot
                            value={overallUploadProgress}
                            size="xs"
                            colorPalette="blue"
                          >
                            <ProgressValueText />
                          </ProgressRoot>
                        ) : (
                          <ProgressRoot
                            value={100}
                            size="xs"
                            colorPalette="blue"
                          >
                            <ProgressValueText />
                          </ProgressRoot>
                        )}
                      </Box>
                    </HStack>
                    {/* Upload progress overlay */}
                    {isUploading && (
                      <Flex
                        position="absolute"
                        top={0}
                        left={0}
                        right={0}
                        bottom={0}
                        bg="rgba(0,0,0,0.25)"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Text color="white" fontSize="xs" fontWeight="bold">
                          Uploading: {overallUploadProgress}%
                        </Text>
                      </Flex>
                    )}
                  </Box>
                )}
                <IconButton
                  aria-label="Remove attachment"
                  size="xs"
                  position="absolute"
                  top={0}
                  right={0}
                  colorPalette="red"
                  variant="solid"
                  onClick={() => removeAttachment(index)}
                  borderRadius="full"
                  m={1}
                  disabled={isDisabled || isUploading}
                >
                  <FiX size={14} />
                </IconButton>
              </Box>
            ))}
          </HStack>
        </Flex>
      )}

      {/* Recording UI */}
      {isRecording && (
        <Flex
          mb={3}
          p={2}
          bg="gray.50"
          borderRadius="md"
          alignItems="center"
          justifyContent="space-between"
        >
          <HStack gap={3} flex="1">
            <Box
              width="10px"
              height="10px"
              borderRadius="full"
              bg="red.500"
              css={{
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                  '100%': { opacity: 1 },
                },
              }}
            />
            <Text fontWeight="medium">
              Recording {formatDuration(recordingDuration)}
            </Text>

            {/* Audio Visualizer */}
            <Box flex="1" height="40px">
              {mediaRecorderRef.current && (
                <LiveAudioVisualizer
                  mediaRecorder={mediaRecorderRef.current}
                  width="100%"
                  height={40}
                  barWidth={2}
                  gap={1}
                  backgroundColor="transparent"
                  barColor="teal.500"
                  smoothingTimeConstant={0.6}
                  minDecibels={-70}
                  maxDecibels={-10}
                />
              )}
            </Box>
          </HStack>

          <HStack gap={2}>
            <IconButton
              aria-label="Cancel recording"
              size="sm"
              colorPalette="red"
              variant="ghost"
              onClick={cancelRecording}
              disabled={isDisabled}
            >
              <FiTrash2 size={16} />
            </IconButton>
            <IconButton
              aria-label="Stop recording"
              size="sm"
              colorPalette="blue"
              onClick={stopRecording}
              disabled={isDisabled}
            >
              <FiSquare size={16} />
            </IconButton>
          </HStack>
        </Flex>
      )}

      {/* Message input area */}
      <Flex
        position="relative"
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        gap={3}
      >
        <Flex
          flex="1"
          direction="column"
          bg={isDisabled ? 'gray.50' : 'gray.100'}
          borderRadius="2xl"
          p={4}
          position="relative"
          border={isDisabled ? '1px dashed' : 'none'}
          borderColor="gray.300"
          _hover={isDisabled ? { borderColor: 'blue.300', bg: 'gray.100' } : {}}
          cursor={isDisabled ? 'pointer' : 'text'}
        >
          {isDisabled && (
            <Flex
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              align="center"
              justify="center"
              zIndex={5}
              bg="gray.50"
              borderRadius="2xl"
            >
              <Text color="blue.500" fontWeight="medium">
                Click to take over conversation from AI Agent
              </Text>
            </Flex>
          )}

          <ResizeTextarea
            placeholder={isDisabled ? '' : 'Type a message...'}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isDisabled || isSending || isUploading}
            minRows={1}
            maxRows={8}
            style={{
              fontSize: '16px',
              border: 'none',
              outline: 'none',
              resize: 'none',
              padding: 0,
              marginBottom: '8px',
              backgroundColor: 'transparent',
              width: '100%',
              fontFamily: 'inherit',
              lineHeight: '1.5',
            }}
            // Prevent event bubbling when disabled but don't add separate click handler
            onClick={(e) => {
              if (isDisabled) {
                e.stopPropagation();
              }
            }}
          />

          <HStack
            justify="flex-end"
            opacity={isDisabled ? 0.5 : 1}
            zIndex={6}
            alignItems="center"
            justifyContent="space-between"
            m={0}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept="image/*,video/*,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
              multiple
              disabled={isDisabled || isSending || isUploading}
            />

            <Avatar
              src={currentUserAvatar}
              name={currentUser.fullName || currentUser.email}
              size="sm"
              opacity={isDisabled ? 0.7 : 1}
            />

            <HStack>
              <IconButton
                aria-label="Attach file"
                variant="ghost"
                color="gray.500"
                _hover={{ bg: isDisabled ? 'gray.400' : 'gray.200' }}
                size="sm"
                borderRadius="full"
                onClick={(e) => {
                  if (isDisabled) {
                    e.stopPropagation();
                  } else {
                    handleFileClick();
                  }
                }}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                bg={isDisabled ? 'gray.50' : 'transparent'}
                disabled={isDisabled || isSending || isUploading}
              >
                <FiPaperclip size={18} />
              </IconButton>
              <IconButton
                aria-label={isRecording ? 'Stop recording' : 'Voice message'}
                variant="ghost"
                color={isRecording ? 'red.500' : 'gray.500'}
                _hover={{ bg: isDisabled ? 'gray.400' : 'gray.200' }}
                size="sm"
                borderRadius="full"
                onClick={(e) => {
                  if (isDisabled) {
                    e.stopPropagation();
                  } else {
                    handleMicClick();
                  }
                }}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                bg={isDisabled ? 'gray.50' : 'transparent'}
                disabled={isDisabled || isSending || isUploading}
              >
                {isRecording ? <FiPause size={18} /> : <FiMic size={18} />}
              </IconButton>
              <IconButton
                aria-label="Send message"
                color="gray"
                bg={isDisabled ? 'gray.300' : 'transparent'}
                _hover={{ bg: isDisabled ? 'gray.400' : 'gray.200' }}
                size="sm"
                borderRadius="full"
                onClick={(e) => {
                  if (isDisabled) {
                    e.stopPropagation();
                  } else {
                    handleSend();
                  }
                }}
                cursor={isDisabled ? 'not-allowed' : 'pointer'}
                loading={isSending || isUploading}
                disabled={isDisabled || isSending || isUploading}
              >
                <FiSend size={18} />
              </IconButton>
            </HStack>
          </HStack>
        </Flex>
      </Flex>
    </Box>
  );
}
